import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import {
  getBranches,
  getEducationalAdministrations,
  getGrades,
  getReferralPartners,
  getSchools,
} from '@/api/lookups.api';
import {
  getStudent,
  registerStudent,
  updateStudent,
  type RegisterStudentPayload,
} from '@/api/students.api';
import { ApiError, type ApiErrorDetail } from '@/api/errors';
import { Banner } from '@/components/common/Banner';
import { FormField } from '@/components/form/FormField';
import { LookupSelect } from '@/components/form/LookupSelect';
import { Stepper } from '@/components/form/Stepper';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import { resolveSingleBranch } from '@/lib/branchScope';
import {
  applicationSourceOptions,
  genderOptions,
  guardianRelationshipOptions,
} from '@/lib/enumOptions';
import {
  isDateOfBirthInUsualRange,
  isPlausibleEgyptianPhone,
  isPlausibleNationalId,
} from '@/lib/validation';
import type { ApplicationSource, Gender, GuardianRelationship, LookupItem } from '@/types/shared';
import './StudentFormPage.css';

interface FormState {
  nameAr: string;
  nameEn: string;
  dateOfBirth: string;
  gender: Gender | '';
  nationalId: string;
  gradeId: number | '';
  agreedSchoolId: number | '';
  educationalAdministrationId: number | '';
  /** T175 (§20, DEC-BR10). CREATE-only — see the render logic below for why an edit-mode value is
   * never read: `branchId` isn't a field on `PATCH /students/{studentCode}` at all (DEC-BRA6). */
  branchId: number | '';
  /** T152 — OPTIONAL "Referred by" partner. `''` = no partner selected. See
   * `referralPartnerTouched` below for how this maps to the wire's undefined/id/null tri-state. */
  referralPartnerId: number | '';
  address: string;
  guardianNameAr: string;
  guardianNameEn: string;
  guardianRelationship: GuardianRelationship | '';
  guardianPhone: string;
  guardianSecondaryPhone: string;
  guardianEmail: string;
  guardianNationalId: string;
  previousSchool: string;
  applicationSource: ApplicationSource | '';
  admissionNotes: string;
  installmentCount: number | '';
  totalAgreedFees: number | '';
}

const EMPTY_FORM: FormState = {
  nameAr: '',
  nameEn: '',
  dateOfBirth: '',
  gender: '',
  nationalId: '',
  gradeId: '',
  agreedSchoolId: '',
  educationalAdministrationId: '',
  branchId: '',
  referralPartnerId: '',
  address: '',
  guardianNameAr: '',
  guardianNameEn: '',
  guardianRelationship: '',
  guardianPhone: '',
  guardianSecondaryPhone: '',
  guardianEmail: '',
  guardianNationalId: '',
  previousSchool: '',
  applicationSource: '',
  admissionNotes: '',
  installmentCount: '',
  totalAgreedFees: '',
};

type FieldErrors = Partial<Record<keyof FormState, string>>;

interface StudentFormPageProps {
  mode: 'create' | 'edit';
}

export function StudentFormPage({ mode }: StudentFormPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { studentCode } = useParams<{ studentCode: string }>();
  const { user } = useAuth();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [fieldWarnings, setFieldWarnings] = useState<FieldErrors>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [grades, setGrades] = useState<LookupItem[]>([]);
  const [schools, setSchools] = useState<LookupItem[]>([]);
  const [eduAdmins, setEduAdmins] = useState<LookupItem[]>([]);
  const [referralPartners, setReferralPartners] = useState<LookupItem[]>([]);
  const [branches, setBranches] = useState<LookupItem[]>([]);

  // T175 (§20, DEC-BR10, `docs/design/19-branch-scoping-ux.md` §3.1) — a single-branch caller
  // never sees the field at all (US-4: registering a student is no slower than today); anyone
  // else gets a required select narrowed to their own scope (the `/lookups/branches` response is
  // already server-scoped, T159 §1.2 — no client-side narrowing is added on top of it).
  const singleBranch = resolveSingleBranch(user);
  const showBranchField = mode === 'create' && !singleBranch;

  // T152 (`docs/architecture/16-referral-partners.md` §14 T152 warning) — the referral picker's
  // three wire states (omit/id/null) cannot be told apart from `form.referralPartnerId` alone,
  // since `''` means BOTH "never had a partner" and "user cleared it back to none". This flag is
  // the sole signal for "the user actually touched this control during this edit session"; only
  // when it's true do we ever send an explicit `null`. Reset on every fresh load (see the
  // initializing effect) so navigating between edits never carries over a stale touch.
  const [referralPartnerTouched, setReferralPartnerTouched] = useState(false);

  const [initializing, setInitializing] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  useEffect(() => {
    let cancelled = false;
    setInitializing(true);
    setInitError(null);
    setReferralPartnerTouched(false);

    const lookupsPromise = Promise.all([
      getGrades({ activeOnly: false }),
      getSchools({ activeOnly: false }),
      getEducationalAdministrations({ activeOnly: false }),
      // `activeOnly: false` — same reason as the three lookups above (10-design-system.md §4.3):
      // a student already linked to a partner that has since been deactivated must still show
      // that partner's name (greyed, forced re-selection if the user wants to change it) rather
      // than silently disappearing from the picker.
      getReferralPartners({ activeOnly: false }),
      // T175 — CREATE-only field (`showBranchField`), but fetched unconditionally like every
      // other lookup here; a cheap, best-effort call that's simply unused on the edit form.
      getBranches({ activeOnly: false }),
    ]);

    const studentPromise =
      mode === 'edit' && studentCode ? getStudent(studentCode) : Promise.resolve(null);

    Promise.all([lookupsPromise, studentPromise])
      .then(([[gradeItems, schoolItems, eduAdminItems, referralPartnerItems, branchItems], student]) => {
        if (cancelled) return;
        setGrades(gradeItems);
        setSchools(schoolItems);
        setEduAdmins(eduAdminItems);
        setReferralPartners(referralPartnerItems);
        setBranches(branchItems);
        if (student) {
          setForm({
            nameAr: student.nameAr,
            nameEn: student.nameEn,
            dateOfBirth: student.dateOfBirth.slice(0, 10),
            gender: student.gender,
            nationalId: student.nationalId ?? '',
            // Never populated in edit mode — `showBranchField` is always false here, and branch
            // change goes through the dedicated transfer action instead (DEC-BRA6).
            branchId: '',
            gradeId: student.grade.id,
            agreedSchoolId: student.agreedSchool.id,
            educationalAdministrationId: student.educationalAdministration.id,
            referralPartnerId: student.referralPartner?.id ?? '',
            address: student.address ?? '',
            guardianNameAr: student.guardianNameAr ?? '',
            guardianNameEn: student.guardianNameEn ?? '',
            guardianRelationship: student.guardianRelationship,
            guardianPhone: student.guardianPhone,
            guardianSecondaryPhone: student.guardianSecondaryPhone ?? '',
            guardianEmail: student.guardianEmail ?? '',
            guardianNationalId: student.guardianNationalId ?? '',
            previousSchool: student.previousSchool ?? '',
            applicationSource: student.applicationSource,
            admissionNotes: student.admissionNotes ?? '',
            installmentCount: '',
            totalAgreedFees: '',
          });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const apiError = ApiError.fromUnknown(err);
        setInitError(
          apiError.code === 'NOT_FOUND'
            ? t('students.detail.notFound')
            : t('students.detail.loadError'),
        );
      })
      .finally(() => {
        if (!cancelled) setInitializing(false);
      });

    return () => {
      cancelled = true;
    };
  }, [mode, studentCode, t]);

  function isLookupInactive(items: LookupItem[], id: number | ''): boolean {
    if (id === '') return false;
    const item = items.find((i) => i.id === id);
    return Boolean(item && !item.isActive);
  }

  function validate(): FieldErrors {
    const errors: FieldErrors = {};
    if (!form.nameAr.trim()) errors.nameAr = t('students.form.errors.nameArRequired');
    if (!form.nameEn.trim()) errors.nameEn = t('students.form.errors.nameEnRequired');
    if (!form.dateOfBirth) errors.dateOfBirth = t('students.form.errors.dateOfBirthRequired');
    if (!form.gender) errors.gender = t('students.form.errors.genderRequired');

    if (showBranchField && form.branchId === '') {
      errors.branchId = t('students.form.branchRequired');
    }

    if (form.gradeId === '') errors.gradeId = t('students.form.errors.gradeRequired');
    else if (isLookupInactive(grades, form.gradeId))
      errors.gradeId = t('students.form.lookupUnavailable');

    if (form.agreedSchoolId === '')
      errors.agreedSchoolId = t('students.form.errors.schoolRequired');
    else if (isLookupInactive(schools, form.agreedSchoolId)) {
      errors.agreedSchoolId = t('students.form.lookupUnavailable');
    }

    if (form.educationalAdministrationId === '') {
      errors.educationalAdministrationId = t(
        'students.form.errors.educationalAdministrationRequired',
      );
    } else if (isLookupInactive(eduAdmins, form.educationalAdministrationId)) {
      errors.educationalAdministrationId = t('students.form.lookupUnavailable');
    }

    // Referral partner is OPTIONAL — no "required" arm, unlike grade/school/eduAdmin above — but
    // an inactive selection is still caught client-side before the round-trip that would 422
    // LOOKUP_VALUE_INACTIVE (the server only validates it when the field is actually present/
    // changed, mirrored by `referralPartnerTouched` at submit time).
    // Gated on `referralPartnerTouched` so this only fires for a partner the user actually picked
    // this session. Without the gate, a student linked to a since-deactivated partner could not be
    // edited AT ALL (an unrelated name/address change would be blocked by a field error on a
    // control the user never touched), forcing them to either reassign — recalculating the
    // recorded cost — or clear it, mutating the books just to save an unrelated edit. The server
    // likewise validates only when the field is actually present in the payload.
    if (
      referralPartnerTouched &&
      form.referralPartnerId !== '' &&
      isLookupInactive(referralPartners, form.referralPartnerId)
    ) {
      errors.referralPartnerId = t('students.form.lookupUnavailable');
    }

    if (!form.guardianRelationship) {
      errors.guardianRelationship = t('students.form.errors.guardianRelationshipRequired');
    }
    if (!form.guardianPhone.trim())
      errors.guardianPhone = t('students.form.errors.guardianPhoneRequired');

    if (
      form.installmentCount !== '' &&
      (!Number.isInteger(form.installmentCount) || form.installmentCount < 1)
    ) {
      errors.installmentCount = t('students.form.errors.installmentCountInvalid');
    }
    if (
      form.totalAgreedFees !== '' &&
      (Number.isNaN(form.totalAgreedFees) || form.totalAgreedFees < 0)
    ) {
      errors.totalAgreedFees = t('students.form.errors.totalAgreedFeesInvalid');
    }

    return errors;
  }

  function checkWarning(field: keyof FormState) {
    setFieldWarnings((prev) => {
      const next = { ...prev };
      delete next[field];

      if (field === 'nationalId' && form.nationalId && !isPlausibleNationalId(form.nationalId)) {
        next.nationalId = t('students.form.warnings.nationalIdFormat');
      }
      if (
        field === 'guardianNationalId' &&
        form.guardianNationalId &&
        !isPlausibleNationalId(form.guardianNationalId)
      ) {
        next.guardianNationalId = t('students.form.warnings.guardianNationalIdFormat');
      }
      if (
        field === 'guardianPhone' &&
        form.guardianPhone &&
        !isPlausibleEgyptianPhone(form.guardianPhone)
      ) {
        next.guardianPhone = t('students.form.warnings.guardianPhoneFormat');
      }
      if (
        field === 'dateOfBirth' &&
        form.dateOfBirth &&
        !isDateOfBirthInUsualRange(form.dateOfBirth)
      ) {
        next.dateOfBirth = t('students.form.warnings.dateOfBirthOutOfRange');
      }
      return next;
    });
  }

  function buildPayload(): RegisterStudentPayload {
    return {
      nameAr: form.nameAr.trim(),
      nameEn: form.nameEn.trim(),
      dateOfBirth: form.dateOfBirth,
      gender: form.gender as Gender,
      nationalId: form.nationalId.trim() || undefined,
      // CREATE-only (`showBranchField` is false for both edit mode and a single-branch caller) —
      // omitted (never `undefined` sent as a key with a value, JSON.stringify drops it) so a
      // single-branch caller's registration lets the server default from their own scope (FR-18),
      // and an edit-mode submission never carries `branchId` at all (DEC-BRA6 — see the explicit
      // destructure-and-drop in the edit branch of `handleSubmit` below, belt and braces).
      branchId: showBranchField && form.branchId !== '' ? form.branchId : undefined,
      gradeId: form.gradeId as number,
      agreedSchoolId: form.agreedSchoolId as number,
      educationalAdministrationId: form.educationalAdministrationId as number,
      // Base (create-oriented) mapping: chosen -> id, none chosen -> omit (`undefined`), NEVER
      // `null` here — on create there is no existing link to clear. The edit branch of
      // `handleSubmit` below overrides this with the full omit/id/null tri-state once it knows
      // whether the user actually touched the control (`referralPartnerTouched`).
      referralPartnerId: form.referralPartnerId === '' ? undefined : form.referralPartnerId,
      address: form.address.trim() || undefined,
      guardianNameAr: form.guardianNameAr.trim() || undefined,
      guardianNameEn: form.guardianNameEn.trim() || undefined,
      guardianRelationship: form.guardianRelationship as GuardianRelationship,
      guardianPhone: form.guardianPhone.trim(),
      guardianSecondaryPhone: form.guardianSecondaryPhone.trim() || undefined,
      guardianEmail: form.guardianEmail.trim() || undefined,
      guardianNationalId: form.guardianNationalId.trim() || undefined,
      previousSchool: form.previousSchool.trim() || undefined,
      applicationSource: form.applicationSource || undefined,
      admissionNotes: form.admissionNotes.trim() || undefined,
      installmentCount: form.installmentCount === '' ? undefined : form.installmentCount,
      totalAgreedFees: form.totalAgreedFees === '' ? undefined : form.totalAgreedFees,
    };
  }

  function applyServerFieldErrors(details: ApiErrorDetail[]) {
    const errors: FieldErrors = {};
    for (const detail of details) {
      if (detail.field && detail.field in EMPTY_FORM) {
        errors[detail.field as keyof FormState] = detail.message;
      }
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors((prev) => ({ ...prev, ...errors }));
      const firstKey = Object.keys(errors)[0];
      document.getElementById(firstKey)?.focus();
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBanner(null);

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      const firstKey = Object.keys(errors)[0];
      document.getElementById(firstKey)?.focus();
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (mode === 'create') {
        const result = await registerStudent(payload);
        const message = t('students.form.createSuccess', { code: result.student.studentCode });
        showSuccessToast(message);
        navigate(`/students/${result.student.studentCode}`, {
          state: {
            successMessage: message,
            warnings: result.warnings,
          },
        });
      } else if (studentCode) {
        // Create-only fields are absent from UpdateStudentDto; with the API's
        // `forbidNonWhitelisted` ValidationPipe, sending them would 400 the whole edit.
        // `branchId` is explicitly dropped here too (DEC-BRA6) — it's already `undefined` in
        // edit-mode's `buildPayload()` output, but this destructure makes the exclusion
        // unmistakable at the one call site that could otherwise regress it.
        const {
          applicationSource: _applicationSource,
          installmentCount: _installmentCount,
          totalAgreedFees: _totalAgreedFees,
          branchId: _branchId,
          ...updatePayload
        } = payload;
        // T152 (`docs/architecture/16-referral-partners.md` §3.1/§14 T152 warning) — the ONLY
        // field on this form whose three wire states are not interchangeable. `buildPayload()`
        // above already omits it when the picker reads "none", but that alone can't distinguish
        // "never had a partner and still doesn't" from "HAD one and the user just cleared it" —
        // both render as `''`. `referralPartnerTouched` is that missing signal:
        //   - untouched  -> delete the key entirely (`undefined` on the wire) -> backend leaves
        //     the existing link/recognized cost alone, no matter what it currently is.
        //   - touched, picker now on a partner -> send that id -> links/recalculates.
        //   - touched, picker now on "— none —" -> send explicit `null` -> CLEARS the link and
        //     removes the recognized referral cost.
        if (referralPartnerTouched) {
          updatePayload.referralPartnerId =
            form.referralPartnerId === '' ? null : form.referralPartnerId;
        } else {
          delete updatePayload.referralPartnerId;
        }
        const updated = await updateStudent(studentCode, updatePayload);
        const message = t('students.form.updateSuccess', { code: updated.studentCode });
        showSuccessToast(message);
        navigate(`/students/${updated.studentCode}`, {
          state: {
            successMessage: message,
          },
        });
      }
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      if (apiError.code === 'NETWORK_ERROR') {
        const message = t('students.form.networkError');
        setBanner(message);
        showErrorToast(message);
      } else if (apiError.code === 'BRANCH_REQUIRED' || apiError.code === 'BRANCH_OUT_OF_SCOPE') {
        // T175 (§20, DEC-BR10) — the server's defense-in-depth backstop behind the client-side
        // `validate()` rule above; carries neither a `field` in `details` (a hand-thrown
        // `AppException`, not a class-validator DTO error) nor is it something this form's normal
        // "any field error" path would map on its own, so it's handled explicitly here.
        setFieldErrors((prev) => ({
          ...prev,
          branchId:
            apiError.code === 'BRANCH_REQUIRED'
              ? t('students.form.branchRequired')
              : t('students.form.errors.branchOutOfScope'),
        }));
        document.getElementById('branchId')?.focus();
      } else {
        setBanner(apiError.message || t('students.form.serverError'));
        if (apiError.details.length > 0) applyServerFieldErrors(apiError.details);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (initializing) {
    return <p className="fa-form-loading">{t('common.loading')}</p>;
  }

  if (initError) {
    return (
      <Banner tone="danger" assertive>
        {initError}
      </Banner>
    );
  }

  const genders = genderOptions(t);
  const relationships = guardianRelationshipOptions(t);
  const sources = applicationSourceOptions(t);

  return (
    <section className="fa-student-form">
      <Link to="/students" className="fa-back-link">
        <span className="fa-back-link__arrow" aria-hidden="true" />
        {t('students.detail.backToList')}
      </Link>
      <h1 className="fa-student-form__title">
        {mode === 'create' ? t('students.form.createTitle') : t('students.form.editTitle')}
      </h1>

      {banner && (
        <Banner tone="danger" assertive>
          {banner}
        </Banner>
      )}

      <p className="fa-required-legend">
        <span className="fa-field__required" aria-hidden="true">
          *
        </span>
        {t('students.form.requiredLegend')}
      </p>

      <form noValidate onSubmit={handleSubmit}>
        <section className="fa-form-section">
          <h2 className="fa-form-section__title">{t('students.form.studentSection')}</h2>
          <p className="fa-form-section__description">
            {t('students.form.studentSectionDescription')}
          </p>

          <div className="fa-field-row">
            <FormField
              id="nameAr"
              label={t('students.form.nameAr')}
              required
              error={fieldErrors.nameAr}
            >
              <input
                type="text"
                value={form.nameAr}
                disabled={submitting}
                onChange={(e) => setField('nameAr', e.target.value)}
              />
            </FormField>
            <FormField
              id="nameEn"
              label={t('students.form.nameEn')}
              required
              error={fieldErrors.nameEn}
            >
              <input
                type="text"
                value={form.nameEn}
                disabled={submitting}
                onChange={(e) => setField('nameEn', e.target.value)}
              />
            </FormField>
          </div>

          <div className="fa-field-row">
            <FormField
              id="dateOfBirth"
              label={t('students.form.dateOfBirth')}
              required
              error={fieldErrors.dateOfBirth}
              warning={fieldWarnings.dateOfBirth}
            >
              <input
                type="date"
                value={form.dateOfBirth}
                disabled={submitting}
                onChange={(e) => setField('dateOfBirth', e.target.value)}
                onBlur={() => checkWarning('dateOfBirth')}
              />
            </FormField>

            <div className="fa-field">
              <span className="fa-field__label">
                {t('students.form.gender')}
                <span className="fa-field__required" aria-hidden="true">
                  {' '}
                  *
                </span>
              </span>
              <div
                className="fa-radio-group"
                role="radiogroup"
                aria-label={t('students.form.gender')}
              >
                {genders.map((option) => (
                  <label key={option.value}>
                    <input
                      type="radio"
                      name="gender"
                      value={option.value}
                      checked={form.gender === option.value}
                      disabled={submitting}
                      onChange={() => setField('gender', option.value)}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              {fieldErrors.gender && <p className="fa-field__error">{fieldErrors.gender}</p>}
            </div>
          </div>

          <FormField
            id="nationalId"
            label={t('students.form.nationalId')}
            error={fieldErrors.nationalId}
            warning={fieldWarnings.nationalId}
          >
            <input
              type="text"
              inputMode="numeric"
              className="fa-ltr-isolate"
              value={form.nationalId}
              disabled={submitting}
              onChange={(e) => setField('nationalId', e.target.value)}
              onBlur={() => checkWarning('nationalId')}
            />
          </FormField>

          <div className="fa-field-row">
            {showBranchField && (
              <FormField
                id="branchId"
                label={t('students.form.branch')}
                required
                error={fieldErrors.branchId}
              >
                <LookupSelect
                  id="branchId"
                  value={form.branchId}
                  onChange={(v) => setField('branchId', v)}
                  items={branches}
                  placeholder={t('students.form.branchPlaceholder')}
                  disabled={submitting}
                />
              </FormField>
            )}
            <FormField
              id="gradeId"
              label={t('students.form.grade')}
              required
              error={fieldErrors.gradeId}
            >
              <LookupSelect
                id="gradeId"
                value={form.gradeId}
                onChange={(v) => setField('gradeId', v)}
                items={grades}
                placeholder={t('students.form.gradePlaceholder')}
                disabled={submitting}
              />
            </FormField>
            <FormField
              id="agreedSchoolId"
              label={t('students.form.school')}
              required
              error={fieldErrors.agreedSchoolId}
            >
              <LookupSelect
                id="agreedSchoolId"
                value={form.agreedSchoolId}
                onChange={(v) => setField('agreedSchoolId', v)}
                items={schools}
                placeholder={t('students.form.schoolPlaceholder')}
                disabled={submitting}
              />
            </FormField>
            <FormField
              id="educationalAdministrationId"
              label={t('students.form.educationalAdministration')}
              required
              error={fieldErrors.educationalAdministrationId}
            >
              <LookupSelect
                id="educationalAdministrationId"
                value={form.educationalAdministrationId}
                onChange={(v) => setField('educationalAdministrationId', v)}
                items={eduAdmins}
                placeholder={t('students.form.educationalAdministrationPlaceholder')}
                disabled={submitting}
              />
            </FormField>
            <FormField
              id="referralPartnerId"
              label={t('students.form.referralPartner')}
              error={fieldErrors.referralPartnerId}
              hint={t('students.form.referralPartnerHint')}
            >
              <LookupSelect
                id="referralPartnerId"
                value={form.referralPartnerId}
                onChange={(v) => {
                  setField('referralPartnerId', v);
                  setReferralPartnerTouched(true);
                }}
                items={referralPartners}
                placeholder={t('students.form.referralPartnerNone')}
                disabled={submitting}
                allowClear
              />
            </FormField>
          </div>

          <FormField id="address" label={t('students.form.address')}>
            <input
              type="text"
              value={form.address}
              disabled={submitting}
              onChange={(e) => setField('address', e.target.value)}
            />
          </FormField>
        </section>

        <section className="fa-form-section">
          <h2 className="fa-form-section__title">{t('students.form.guardianSection')}</h2>
          <p className="fa-form-section__description">
            {t('students.form.guardianSectionDescription')}
          </p>

          <div className="fa-field-row">
            <FormField id="guardianNameAr" label={t('students.form.guardianNameAr')}>
              <input
                type="text"
                value={form.guardianNameAr}
                disabled={submitting}
                onChange={(e) => setField('guardianNameAr', e.target.value)}
              />
            </FormField>
            <FormField id="guardianNameEn" label={t('students.form.guardianNameEn')}>
              <input
                type="text"
                value={form.guardianNameEn}
                disabled={submitting}
                onChange={(e) => setField('guardianNameEn', e.target.value)}
              />
            </FormField>
          </div>

          <div className="fa-field-row">
            <FormField
              id="guardianRelationship"
              label={t('students.form.guardianRelationship')}
              required
              error={fieldErrors.guardianRelationship}
            >
              <select
                value={form.guardianRelationship}
                disabled={submitting}
                onChange={(e) =>
                  setField('guardianRelationship', e.target.value as GuardianRelationship)
                }
              >
                <option value="" disabled>
                  {t('students.form.guardianRelationshipPlaceholder')}
                </option>
                {relationships.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField
              id="guardianPhone"
              label={t('students.form.guardianPhone')}
              required
              error={fieldErrors.guardianPhone}
              warning={fieldWarnings.guardianPhone}
            >
              <input
                type="tel"
                className="fa-ltr-isolate"
                value={form.guardianPhone}
                disabled={submitting}
                onChange={(e) => setField('guardianPhone', e.target.value)}
                onBlur={() => checkWarning('guardianPhone')}
              />
            </FormField>
          </div>

          <div className="fa-field-row">
            <FormField
              id="guardianSecondaryPhone"
              label={t('students.form.guardianSecondaryPhone')}
            >
              <input
                type="tel"
                className="fa-ltr-isolate"
                value={form.guardianSecondaryPhone}
                disabled={submitting}
                onChange={(e) => setField('guardianSecondaryPhone', e.target.value)}
              />
            </FormField>
            <FormField id="guardianEmail" label={t('students.form.guardianEmail')}>
              <input
                type="email"
                className="fa-ltr-isolate"
                value={form.guardianEmail}
                disabled={submitting}
                onChange={(e) => setField('guardianEmail', e.target.value)}
              />
            </FormField>
          </div>

          <FormField
            id="guardianNationalId"
            label={t('students.form.guardianNationalId')}
            warning={fieldWarnings.guardianNationalId}
          >
            <input
              type="text"
              inputMode="numeric"
              className="fa-ltr-isolate"
              value={form.guardianNationalId}
              disabled={submitting}
              onChange={(e) => setField('guardianNationalId', e.target.value)}
              onBlur={() => checkWarning('guardianNationalId')}
            />
          </FormField>
        </section>

        <section className="fa-form-section">
          <h2 className="fa-form-section__title">{t('students.form.additionalSection')}</h2>
          <p className="fa-form-section__description">
            {t('students.form.additionalSectionDescription')}
          </p>

          <FormField id="previousSchool" label={t('students.form.previousSchool')}>
            <input
              type="text"
              value={form.previousSchool}
              disabled={submitting}
              onChange={(e) => setField('previousSchool', e.target.value)}
            />
          </FormField>

          <FormField id="applicationSource" label={t('students.form.applicationSource')}>
            <select
              value={form.applicationSource}
              disabled={submitting}
              onChange={(e) => setField('applicationSource', e.target.value as ApplicationSource)}
            >
              <option value="">{t('students.form.applicationSourcePlaceholder')}</option>
              {sources.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </FormField>

          <FormField id="admissionNotes" label={t('students.form.admissionNotes')}>
            <textarea
              value={form.admissionNotes}
              disabled={submitting}
              onChange={(e) => setField('admissionNotes', e.target.value)}
            />
          </FormField>

          <div className="fa-field-row">
            <div className="fa-field">
              <span className="fa-field__label" id="installmentCount-label">
                {t('students.form.installmentCount')}
              </span>
              <Stepper
                id="installmentCount"
                value={form.installmentCount}
                min={1}
                onChange={(v) => setField('installmentCount', v)}
                decrementLabel={t('table.pagination.previous')}
                incrementLabel={t('table.pagination.next')}
              />
              {fieldErrors.installmentCount && (
                <p className="fa-field__error">{fieldErrors.installmentCount}</p>
              )}
            </div>

            <FormField
              id="totalAgreedFees"
              label={t('students.form.totalAgreedFees')}
              error={fieldErrors.totalAgreedFees}
            >
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.totalAgreedFees}
                disabled={submitting}
                onChange={(e) =>
                  setField('totalAgreedFees', e.target.value === '' ? '' : Number(e.target.value))
                }
              />
            </FormField>
          </div>
        </section>

        <div className="fa-form-actions fa-form-actions--sticky">
          <button
            type="button"
            className="fa-btn fa-btn--secondary"
            disabled={submitting}
            onClick={() => navigate(-1)}
          >
            {t('students.form.cancel')}
          </button>
          <button type="submit" className="fa-btn fa-btn--primary" disabled={submitting}>
            {submitting
              ? t('students.form.submitting')
              : mode === 'create'
                ? t('students.form.submitCreate')
                : t('students.form.submitEdit')}
          </button>
        </div>
      </form>
    </section>
  );
}
